import { ResolvedMatch, Employee } from '../interfaces/interface';

/**
 * Normalizes a login name to lowercase for consistent comparison.
 */
export const normalizeLoginName = (employee: Employee): string => {
  return employee.login_name?.toLowerCase() ?? '';
};

/**
 * Checks if an employee already exists in the attendees list based on login name.
 */
export const isDuplicateEmployee = (employee: Employee, currentAttendees: ResolvedMatch[]): boolean => {
  const loginName = normalizeLoginName(employee);
  if (!loginName) return false;

  return currentAttendees.some((attendee) => {
    const existingLoginName = attendee.selectedEmployee?.login_name?.toLowerCase();
    return existingLoginName === loginName;
  });
};

/**
 * Sorts attendees by the following criteria:
 * 1. Unresolved attendees first (need attention)
 * 2. Job level (higher levels first - L8 before L6)
 * 3. Priority (required before optional)
 * 4. Alphabetical by name
 */
export const sortAttendees = (attendees: ResolvedMatch[]): ResolvedMatch[] => {
  return [...attendees].sort((a, b) => {
    const aResolved = a.selectedEmployee;
    const bResolved = b.selectedEmployee;

    // Unresolved attendees first (need attention)
    if (!aResolved && bResolved) return -1;
    if (!bResolved && aResolved) return 1;

    // If both are unresolved, maintain original order
    if (!aResolved && !bResolved) return 0;

    // Both are resolved - apply sorting logic
    if (aResolved && bResolved) {
      // Job level (higher levels first)
      const aLevel = parseInt(a.selectedEmployee?.job_level_name || '0');
      const bLevel = parseInt(b.selectedEmployee?.job_level_name || '0');
      if (aLevel !== bLevel) return bLevel - aLevel;

      // Priority (required before optional)
      const priorityOrder = { required: 1, optional: 2 };
      if (a.priority !== b.priority) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }

      // Alphabetical by name
      const aName = a.selectedEmployee?.employee_name || a.inputName;
      const bName = b.selectedEmployee?.employee_name || b.inputName;
      return aName.localeCompare(bName);
    }

    return 0;
  });
};

/**
 * Finds duplicate email addresses in the attendees list.
 */
export const findDuplicateEmails = (attendees: ResolvedMatch[]): Set<string> => {
  const emailCounts = new Map<string, number>();
  const duplicates = new Set<string>();

  attendees.forEach((attendee) => {
    const email = attendee.resolvedEmail?.toLowerCase();
    if (email) {
      const count = emailCounts.get(email) || 0;
      emailCounts.set(email, count + 1);
      if (count >= 1) {
        duplicates.add(email);
      }
    }
  });

  return duplicates;
};

/**
 * Gets all existing email addresses from attendees.
 */
export const getExistingEmails = (attendees: ResolvedMatch[]): Set<string> => {
  return new Set(attendees.map((a) => a.resolvedEmail?.toLowerCase()).filter(Boolean) as string[]);
};

/**
 * Checks if there are any unresolved attendee names.
 */
export const hasUnresolvedNames = (attendees: ResolvedMatch[]): boolean => {
  return attendees.some((attendee) => !attendee.selectedEmployee);
};
